import React, { useMemo } from 'react';
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import { useGetReportPaymentBreakdown } from '../../hooks/reports/useGetReportPaymentBreakdown';
import useCurrency from '../../hooks/common/useCurrency';
import { formatNumber } from '../../utils/money';

const PROVIDER_LABELS = {
    cod:        { en: 'Cash on Delivery', ar: 'الدفع عند الاستلام' },
    paymob:     { en: 'Paymob', ar: 'باي موب' },
    manual:     { en: 'Manual', ar: 'يدوي' },
    knet:       { en: 'KNET', ar: 'كي نت' },
    tap:        { en: 'Tap', ar: 'تاب' },
    myfatoorah: { en: 'MyFatoorah', ar: 'ماي فاتورة' },
    visa:       { en: 'Visa / Mastercard', ar: 'فيزا / ماستر كارد' },
};

const PaymentBreakdown = ({ filter }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();
    const { format: formatPrice, locale } = useCurrency();

    const { items, isLoading } = useGetReportPaymentBreakdown({
        from: filter.from,
        to: filter.to,
    });

    const colorPool = useMemo(
        () => [
            theme.palette.primary.main,
            theme.palette.success.main,
            theme.palette.info.main,
            theme.palette.warning.main,
            theme.palette.secondary.main,
            theme.palette.error.main,
        ],
        [theme]
    );

    const data = useMemo(
        () =>
            items.map((row, idx) => {
                const provider = row.provider || row.payment_method || 'unknown';
                const labels = PROVIDER_LABELS[provider];
                return {
                    provider,
                    name: labels ? (isArabic ? labels.ar : labels.en) : provider,
                    amount: Number(row.amount ?? row.total ?? row.count) || 0,
                    count: Number(row.count) || 0,
                    color: colorPool[idx % colorPool.length],
                };
            }),
        [items, isArabic, colorPool]
    );

    const totalAmount = data.reduce((s, d) => s + d.amount, 0);
    const totalCount = data.reduce((s, d) => s + d.count, 0);
    const isEmpty = !isLoading && totalAmount === 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'توزيع المدفوعات حسب البوابة' : 'Payments by gateway'}
            </Typography>

            {isLoading ? (
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : isEmpty ? (
                <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                        {isArabic ? 'لا توجد بيانات' : 'No data'}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ width: '100%', height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="amount"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={95}
                                    paddingAngle={2}
                                >
                                    {data.map((entry) => (
                                        <Cell key={entry.provider} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 8,
                                    }}
                                    formatter={(value, name) => [formatPrice(value), name]}
                                />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    wrapperStyle={{ fontSize: 12 }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                        {isArabic ? 'الإجمالي: ' : 'Total: '}
                        {formatPrice(totalAmount)}
                        {' • '}
                        {formatNumber(totalCount, locale)}{' '}
                        {isArabic ? 'عملية' : 'transactions'}
                    </Typography>
                </>
            )}
        </Paper>
    );
};

export default PaymentBreakdown;
